import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  BanknoteArrowUp,
  Check,
  ClipboardCheck,
  Clock,
  Copy,
  Landmark,
  Wallet,
  X,
} from 'lucide-react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  StatTile,
} from '../components/ui';
import { api, compact } from '../lib/api';
import {
  formatCredits,
  formatDateTime,
  formatMoneyMinor,
  formatNumber,
} from '../lib/format';
import { useT } from '../lib/i18n';
import { useOpenRecord } from '../lib/navigation';
import { errorMessage, useToast } from '../lib/toast';
import type {
  Paginated,
  Withdrawal,
  WithdrawalStatus,
  WithdrawalTotals,
} from '../lib/types';

const STATUS_TONES: Record<
  WithdrawalStatus,
  'neutral' | 'brand' | 'good' | 'warn' | 'danger'
> = {
  pending: 'warn',
  approved: 'brand',
  paid: 'good',
  rejected: 'danger',
  cancelled: 'neutral',
};

/**
 * The payout queue.
 *
 * Every transfer here is made by hand in a banking app, so this page is not a
 * button that sends money — it is the checklist an operator works through and
 * the record of what they decided. Three things have to be on screen together
 * for that to be possible: the amount, the account it is going to, and who is
 * asking. A page that made the operator open a drawer to see the IBAN would be
 * a page that gets transfers wrong.
 *
 * ## The two-step decision
 *
 * *Approve* says "I have checked this and it is good to send"; *paid* says "I
 * have sent it, here is the bank's reference". They are separate because they
 * happen at different times — the queue is cleared in the morning and the
 * transfers are made in a batch — and because the reference only exists once
 * the money has moved. The backend will not accept a payment on a request
 * nobody approved.
 */
export function WithdrawalsPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);

  // The queue's default is what still needs doing. An operator opening this
  // page is almost never looking for last month's paid rows.
  const status = params.get('status') ?? '';
  const openOnly = status === '' ? true : undefined;

  const query = useMemo(
    () => compact({ status: status || undefined, openOnly, page, limit: 25 }),
    [status, openOnly, page],
  );

  const totals = useQuery({
    queryKey: ['withdrawals', 'totals'],
    queryFn: () => api.get<WithdrawalTotals>('/api/withdrawals/totals'),
  });

  const requests = useQuery({
    queryKey: ['withdrawals', query],
    queryFn: () => api.get<Paginated<Withdrawal>>('/api/withdrawals', query),
  });

  const setStatus = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('status', value);
    else next.delete('status');
    setParams(next, { replace: true });
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title={t('withdrawals.title')}
        description={t('withdrawals.description')}
      />

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {totals.isPending || !totals.data ? (
            Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-24" />
            ))
          ) : (
            <>
              <StatTile
                tone={totals.data.pending > 0 ? 'warn' : 'neutral'}
                icon={<Clock size={14} />}
                label={t('withdrawals.tile.pending')}
                value={formatNumber(totals.data.pending)}
                caption={t('withdrawals.tile.pendingCaption')}
              />
              <StatTile
                tone="brand"
                icon={<ClipboardCheck size={14} />}
                label={t('withdrawals.tile.approved')}
                value={formatNumber(totals.data.approved)}
                caption={t('withdrawals.tile.approvedCaption')}
              />
              <StatTile
                icon={<Wallet size={14} />}
                label={t('withdrawals.tile.open')}
                value={formatMoneyMinor(totals.data.openMinorUnits)}
                caption={t('withdrawals.tile.openCaption')}
              />
              <StatTile
                tone="good"
                icon={<BanknoteArrowUp size={14} />}
                label={t('withdrawals.tile.paid')}
                value={formatMoneyMinor(totals.data.paidMinorUnits)}
                caption={t('withdrawals.tile.paidCaption')}
              />
            </>
          )}
        </div>
        {totals.isError && <ErrorNote error={totals.error} />}

        <div className="rounded-xl border border-line bg-surface p-3.5 sm:p-4">
          <Field label={t('withdrawals.filter.status')}>
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">{t('withdrawals.filter.openOnly')}</option>
              {(Object.keys(STATUS_TONES) as WithdrawalStatus[]).map((value) => (
                <option key={value} value={value}>
                  {t(`withdrawals.status.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {requests.isError && <ErrorNote error={requests.error} />}

        <Card>
          {requests.isPending ? (
            <Skeleton className="h-96" />
          ) : (requests.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              icon={<BanknoteArrowUp size={28} />}
              title={t('withdrawals.emptyTitle')}
              description={t('withdrawals.emptyDescription')}
            />
          ) : (
            <>
              <ul className="divide-y divide-line">
                {requests.data?.items.map((request) => (
                  <WithdrawalRow key={request.id} request={request} />
                ))}
              </ul>
              <Pagination
                page={requests.data?.page ?? 1}
                totalPages={requests.data?.totalPages ?? 1}
                total={requests.data?.total ?? 0}
                onChange={setPage}
              />
            </>
          )}
        </Card>
      </div>
    </>
  );
}

type Decision = 'approve' | 'reject' | 'pay';

/**
 * One request, with everything needed to make the transfer on the row itself.
 *
 * Laid out as a stack that becomes two columns on a wide screen rather than a
 * table: the IBAN and the holder name are long, and a table would either clip
 * them or scroll sideways on the phone an operator is holding while they are
 * in their banking app.
 */
function WithdrawalRow({ request }: { request: Withdrawal }) {
  const t = useT();
  const toast = useToast();
  const openRecord = useOpenRecord();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reference, setReference] = useState('');

  const decide = useMutation({
    mutationFn: ({ action, reason }: { action: Decision; reason: string }) =>
      api.post<Withdrawal>(`/api/withdrawals/${request.id}/${action}`, {
        ...(action === 'reject' ? { reason } : {}),
        ...(action === 'pay' ? { reference: reference.trim() } : {}),
        ...(action !== 'reject' && reason ? { note: reason } : {}),
      }),
    onSuccess: (_, { action }) => {
      toast.success(t(`withdrawals.toast.${action}`));
      setDecision(null);
      setReference('');
      void queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const copyIban = () => {
    void navigator.clipboard
      ?.writeText(request.iban)
      .then(() => toast.success(t('withdrawals.ibanCopied')))
      .catch(() => toast.error(t('withdrawals.ibanCopyFailed')));
  };

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold">
              {formatMoneyMinor(request.amountMinorUnits)}
            </span>
            <span className="text-xs text-muted">
              {formatCredits(request.daylights)}
            </span>
            <Badge tone={STATUS_TONES[request.status]}>
              {t(`withdrawals.status.${request.status}`)}
            </Badge>
          </div>

          {/* The bank details, as one block, because they are used together. */}
          <div className="rounded-lg border border-line bg-canvas px-3 py-2">
            <div className="flex items-center gap-2 text-xs text-muted">
              <Landmark size={13} />
              {request.holderName}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <code className="min-w-0 truncate font-mono text-xs sm:text-sm">
                {request.iban}
              </code>
              <Button
                variant="ghost"
                onClick={copyIban}
                icon={<Copy size={13} />}
                aria-label={t('withdrawals.copyIban')}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <IdChip id={request.userId} title={t('withdrawals.userId')} />
            <Button
              variant="ghost"
              onClick={() => openRecord({ kind: 'user', id: request.userId })}
            >
              {t('withdrawals.openUser')}
            </Button>
            <span>{formatDateTime(request.requestedAt)}</span>
          </div>

          {request.reviewedBy && (
            <Row label={t('withdrawals.reviewedBy')}>
              {`${request.reviewedBy} · ${formatDateTime(request.reviewedAt)}`}
            </Row>
          )}
          {request.paymentReference && (
            <Row label={t('withdrawals.reference')}>{request.paymentReference}</Row>
          )}
          {request.rejectionReason && (
            <Row label={t('withdrawals.rejectionReason')}>{request.rejectionReason}</Row>
          )}
          {request.reviewNote && (
            <Row label={t('withdrawals.note')}>{request.reviewNote}</Row>
          )}
        </div>

        {/* Only the decisions this request can actually take, so the order the
            flow has to happen in is visible rather than enforced by an error. */}
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {request.status === 'pending' && (
            <Button
              variant="primary"
              icon={<Check size={14} />}
              onClick={() => setDecision('approve')}
            >
              {t('withdrawals.action.approve')}
            </Button>
          )}
          {request.status === 'approved' && (
            <Button
              variant="primary"
              icon={<BanknoteArrowUp size={14} />}
              onClick={() => setDecision('pay')}
            >
              {t('withdrawals.action.pay')}
            </Button>
          )}
          {(request.status === 'pending' || request.status === 'approved') && (
            <Button
              variant="danger"
              icon={<X size={14} />}
              onClick={() => setDecision('reject')}
            >
              {t('withdrawals.action.reject')}
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={decision !== null}
        title={decision ? t(`withdrawals.confirm.${decision}.title`) : ''}
        description={
          decision
            ? t(`withdrawals.confirm.${decision}.description`, {
                amount: formatMoneyMinor(request.amountMinorUnits),
                iban: request.maskedIban,
              })
            : undefined
        }
        confirmLabel={decision ? t(`withdrawals.action.${decision}`) : undefined}
        destructive={decision === 'reject'}
        loading={decide.isPending}
        onCancel={() => {
          setDecision(null);
          setReference('');
        }}
        onConfirm={(reason) => {
          if (!decision) return;
          // The reference is what a transfer is reconciled against later, so
          // the dialog will not close without one.
          if (decision === 'pay' && reference.trim().length < 3) {
            toast.error(t('withdrawals.referenceRequired'));
            return;
          }
          if (decision === 'reject' && reason.trim().length < 3) {
            toast.error(t('withdrawals.reasonRequired'));
            return;
          }
          decide.mutate({ action: decision, reason });
        }}
      >
        {decision === 'pay' && (
          <Field
            label={t('withdrawals.reference')}
            hint={t('withdrawals.referenceHint')}
          >
            <Input
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder={t('withdrawals.referencePlaceholder')}
            />
          </Field>
        )}
      </ConfirmDialog>
    </li>
  );
}
